import { auth } from './firebase.js'

const BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8080'

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function getToken(): Promise<string> {
  const user = auth.currentUser
  if (!user) throw new ApiError('UNAUTHENTICATED', 'Not signed in', 401)
  return user.getIdToken()
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token  = await getToken()
  const res    = await fetch(BASE + path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers ?? {}),
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { code?: string; message?: string }
    throw new ApiError(body.code ?? 'UNKNOWN', body.message ?? res.statusText, res.status)
  }
  // 204 No Content
  if (res.status === 204) return undefined as unknown as T
  return res.json() as Promise<T>
}

// ── Types ──────────────────────────────────────────────────────────────────────

export interface MixSettings {
  duckPercent:    number
  originalVolume: number
  dubbedVolume?:  number
}

export interface Job {
  id:                 string
  userId:             string
  videoUrl:           string
  videoTitle:         string
  videoThumbnailUrl:  string
  sourceLang:         string
  targetLang:         string
  durationSeconds:    number
  status:             string
  steps:              JobStep[]
  originalStoragePath: string | null
  outputStoragePath:  string | null
  outputUrl:          string | null
  captureRate:        number | null
  creditsCost:        number
  failureCode:        string | null
  downloadedAt:       number | null
  mixSettings?:       MixSettings
  createdAt:          number
  expiresAt:          number
}
export interface JobStep {
  name:        string
  status:      string
  startedAt:   number | null
  completedAt: number | null
  error:       string | null
}
export interface CreditBalance {
  studioMinutesTotal:        number
  studioMinutesUsed:         number
  studioMinutesReserved:     number
  extensionMinutesTotal:     number
  extensionMinutesUsed:      number
  studioTrialMinutesUsed:    number
  extensionTrialMinutesUsed: number
  topupMinutesTotal:         number
  topupMinutesUsed:          number
  freeMinutesResetAt:        number
}
export interface Subscription {
  tier:             string
  status:           string
  currentPeriodEnd: number
  cancelAtPeriodEnd: boolean
  stripeCustomerId: string
}
export interface AppNotification {
  id:        string
  userId:    string
  type:      'job_ready' | 'job_failed' | 'expiry_warning' | 'payment_success'
  title:     string
  body:      string
  read:      boolean
  jobId?:    string
  createdAt: number
}

export interface UserProfile {
  uid:           string
  email:         string
  displayName:   string
  preferredLang: string
  photoUrl:      string | null
}

// ── Jobs ───────────────────────────────────────────────────────────────────────

export interface StartCaptureJobResult {
  id:          string
  status:      string
  uploadUrl:   string
  storagePath: string
}

export async function startCaptureJob(params: {
  targetLang:        string
  captureRate:       number
  videoUrl?:         string
  videoTitle?:       string
  videoThumbnailUrl?: string
  sourceLang?:       string
  mixSettings?:      MixSettings
}): Promise<StartCaptureJobResult> {
  return apiFetch('/jobs/capture', {
    method: 'POST',
    body:   JSON.stringify(params),
  })
}

export async function completeCaptureJob(jobId: string): Promise<void> {
  return apiFetch(`/jobs/${jobId}/captured`, { method: 'PATCH' })
}

export async function submitJob(
  videoUrl: string,
  targetLang: string,
  mixSettings?: MixSettings,
): Promise<{ id: string; status: string }> {
  return apiFetch('/jobs', {
    method: 'POST',
    body:   JSON.stringify({ videoUrl, targetLang, mixSettings }),
  })
}

export async function fetchJobs(limit = 20, cursor?: string): Promise<{ jobs: Job[]; nextCursor: string | null }> {
  const qs = new URLSearchParams({ limit: String(limit) })
  if (cursor) qs.set('cursor', cursor)
  return apiFetch(`/jobs?${qs}`)
}

export async function fetchJob(id: string): Promise<Job> {
  return apiFetch(`/jobs/${id}`)
}

export async function removeJob(id: string): Promise<void> {
  return apiFetch(`/jobs/${id}`, { method: 'DELETE' })
}

export async function cancelJob(id: string): Promise<void> {
  await apiFetch(`/jobs/${id}/cancel`, { method: 'POST' })
}

export async function retryJob(id: string): Promise<{ id: string; status: string; resumeStep: string }> {
  return apiFetch(`/jobs/${id}/retry`, { method: 'POST' })
}

export async function markDownloaded(id: string): Promise<void> {
  return apiFetch(`/jobs/${id}/downloaded`, { method: 'POST' })
}

export interface WordTimestamp {
  word:         string
  startSeconds: number
  endSeconds:   number
}

export interface CaptionSegment {
  startSeconds: number
  endSeconds:   number
  text:         string
  words?:       WordTimestamp[]
}

export async function fetchCaptions(id: string): Promise<{ captions: CaptionSegment[] }> {
  return apiFetch(`/jobs/${id}/captions`)
}

// ── Account ────────────────────────────────────────────────────────────────────

export async function fetchProfile(): Promise<UserProfile> {
  return apiFetch('/users/me')
}

export async function updateProfile(data: { displayName?: string; preferredLang?: string }): Promise<void> {
  return apiFetch('/users/me', { method: 'PATCH', body: JSON.stringify(data) })
}

export async function deleteAccount(): Promise<void> {
  return apiFetch('/users/me', { method: 'DELETE' })
}

export async function fetchCredits(): Promise<CreditBalance> {
  return apiFetch('/credits')
}

export async function fetchSubscription(): Promise<Subscription> {
  return apiFetch('/subscriptions/me')
}

// ── Payments ───────────────────────────────────────────────────────────────────

export async function createCheckout(tier: string, successUrl: string, cancelUrl: string): Promise<{ sessionId: string; url: string }> {
  return apiFetch('/payments/stripe/checkout', {
    method: 'POST',
    body:   JSON.stringify({ tier, successUrl, cancelUrl }),
  })
}

export async function createTopupCheckout(topupMinutes: number, successUrl: string, cancelUrl: string): Promise<{ sessionId: string; url: string }> {
  return apiFetch('/payments/stripe/checkout', {
    method: 'POST',
    body:   JSON.stringify({ topupMinutes, successUrl, cancelUrl }),
  })
}

export async function createPortalSession(returnUrl: string): Promise<{ url: string }> {
  return apiFetch(`/payments/stripe/portal?returnUrl=${encodeURIComponent(returnUrl)}`)
}

export interface PaymentRecord {
  id:              string
  userId:          string
  amount:          number  // smallest currency unit (cents for USD)
  currency:        string  // ISO 4217
  description:     string
  paymentProvider: string
  externalTxId:    string
  status:          'succeeded' | 'pending' | 'failed' | 'refunded'
  createdAt:       number  // Unix ms
}

export async function fetchPaymentHistory(limit = 20, cursor?: string): Promise<{ history: PaymentRecord[]; nextCursor: string | null }> {
  const qs = new URLSearchParams({ limit: String(limit) })
  if (cursor) qs.set('cursor', cursor)
  return apiFetch(`/payments/history?${qs}`)
}

// ── Notifications ───────────────────────────────────────────────────────────────

export async function fetchNotifications(): Promise<{ notifications: AppNotification[] }> {
  return apiFetch('/notifications')
}

export async function markNotificationRead(id: string): Promise<void> {
  return apiFetch(`/notifications/${id}/read`, { method: 'PATCH' })
}

export async function deleteNotification(id: string): Promise<void> {
  return apiFetch(`/notifications/${id}`, { method: 'DELETE' })
}
